// src/components/lesson/diagrams/Cylinder.tsx
//
// Renders a labeled right cylinder (a "can shape") using SVG. Used by
// lessons and the AI Tutor when a Volume question (V = πr²h) needs a
// picture.
//
// Visual structure:
//   - Top ellipse:    full, solid stroke (the visible top rim)
//   - Bottom ellipse: front half solid, back half dashed (hidden edge)
//   - Two vertical lines connecting the rim ends
//
// Params shape:
//   {
//     "radius": 2,        // proportional
//     "height": 5,        // proportional
//     "radiusLabel": "r", // text rendered on the radius line (or "3", "3 cm")
//     "heightLabel": "h",
//     "showHiddenEdges": true,  // dashed back-half of bottom ellipse
//     "label": "Cylinder"       // accessible name
//   }

type Params = {
  radius?: number
  height?: number
  radiusLabel?: string
  heightLabel?: string
  showHiddenEdges?: boolean
  label?: string
}

// Canvas size matches FunctionGraph + RectangularPrism for visual
// consistency across diagram types.
const W = 360
const H = 240
const PAD = 30

// Ellipse "squish" factor — how flat the top/bottom rims look.
// 0.30 gives a recognizable 3D feel without making the top look
// near-edge-on or fully flat. Adjust if the cylinder reads too
// disc-y or too rectangular.
const ELLIPSE_SQUISH = 0.3

export default function Cylinder({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const radius = typeof p.radius === 'number' && p.radius > 0 ? p.radius : 2
  const height = typeof p.height === 'number' && p.height > 0 ? p.height : 5
  const showHidden = p.showHiddenEdges !== false
  const radiusLabel = p.radiusLabel ?? 'r'
  const heightLabel = p.heightLabel ?? 'h'

  // Compute pixel sizes. We choose the ratio between rx (the
  // horizontal radius of the top/bottom ellipses, in pixels) and
  // pixHeight (the vertical span of the side wall) so the cylinder
  // looks right at any input ratio:
  //
  //   - Tall + thin (h=10, r=1): pixHeight tall, rx narrow
  //   - Short + wide (h=1, r=5): pixHeight short, rx wide
  //
  // The longest dimension fills the canvas budget; the other one
  // scales proportionally. The bottom ellipse's vertical squish
  // (ry = rx * ELLIPSE_SQUISH) is the same regardless — that's a
  // viewing-angle constant, not data-dependent.
  const usableH = H - 2 * PAD
  const usableW = W - 2 * PAD
  const targetRxByH = (usableH * radius) / (height + 2 * ELLIPSE_SQUISH * radius) // height-bound
  const targetRxByW = usableW / 2 // width-bound (rx is half the cylinder width)
  const rx = Math.max(20, Math.min(targetRxByH, targetRxByW))
  const ry = rx * ELLIPSE_SQUISH
  const pixHeight = (rx * height) / radius

  // Center of the top ellipse + center of the bottom ellipse.
  const cx = W / 2
  const topCy = (H - pixHeight) / 2 + ry
  const bottomCy = topCy + pixHeight

  // Stroke styling matches RectangularPrism / FunctionGraph for
  // a coherent chart aesthetic.
  const stroke = 'rgb(34 211 238)' // cyan-400
  const strokeW = 1.8
  const fill = 'rgb(34 211 238 / 0.08)'

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Cylinder'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Side wall — a rectangle between the rim heights, with a
          subtle fill so the cylinder reads as a solid shape rather
          than a wireframe. The vertical edges (left/right) get
          drawn explicitly because the rectangle's stroke would
          paint over the top/bottom ellipses; we want the ellipses
          to own the top and bottom outlines. */}
      <rect x={cx - rx} y={topCy} width={rx * 2} height={pixHeight} fill={fill} stroke="none" />
      <line
        x1={cx - rx}
        y1={topCy}
        x2={cx - rx}
        y2={bottomCy}
        stroke={stroke}
        strokeWidth={strokeW}
      />
      <line
        x1={cx + rx}
        y1={topCy}
        x2={cx + rx}
        y2={bottomCy}
        stroke={stroke}
        strokeWidth={strokeW}
      />

      {/* Bottom ellipse — front half is the solid visible curve;
          back half is dashed (it's behind the cylinder from this
          angle). Using two arc paths (one solid, one dashed) since
          SVG <ellipse> can't be half-styled. */}
      <path
        d={`M ${cx - rx} ${bottomCy} A ${rx} ${ry} 0 0 0 ${cx + rx} ${bottomCy}`}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW}
      />
      {showHidden && (
        <path
          d={`M ${cx - rx} ${bottomCy} A ${rx} ${ry} 0 0 1 ${cx + rx} ${bottomCy}`}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeW * 0.7}
          strokeDasharray="4 3"
        />
      )}

      {/* Top ellipse — fully visible (we're looking down at it from
          a slight angle), solid stroke. */}
      <ellipse
        cx={cx}
        cy={topCy}
        rx={rx}
        ry={ry}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeW}
      />

      {/* Radius indicator — a line from center to right edge of the
          top ellipse, with the radius label. The center dot anchors
          it visually. */}
      <circle cx={cx} cy={topCy} r="2" fill={stroke} />
      <line
        x1={cx}
        y1={topCy}
        x2={cx + rx}
        y2={topCy}
        stroke="rgb(251 191 36)"
        strokeWidth={strokeW * 0.8}
      />
      <text
        x={cx + rx / 2}
        y={topCy - 4}
        fontSize="12"
        fontWeight="600"
        fill="rgb(251 191 36)"
        textAnchor="middle"
      >
        {radiusLabel}
      </text>

      {/* Height indicator — a label on the right side of the
          vertical side wall, near vertical-middle. Offset to the
          right of the cylinder edge so it doesn't sit on top of
          the stroke. */}
      <text
        x={cx + rx + 8}
        y={(topCy + bottomCy) / 2 + 4}
        fontSize="12"
        fontWeight="600"
        fill="rgb(251 191 36)"
      >
        {heightLabel}
      </text>
    </svg>
  )
}
